import { ApplicationConfig, Injectable, inject, provideAppInitializer, provideZonelessChangeDetection } from '@angular/core';
import { HttpClient, provideHttpClient } from '@angular/common/http';
import { CDK_DRAG_CONFIG } from '@angular/cdk/drag-drop';
import { provideI18n } from '@corporate/ng-tebis-base-components';
import { provideOAuthClient } from 'angular-oauth2-oidc';
import { catchError, firstValueFrom, of } from 'rxjs';

import { Activation, provideMDIRouter } from './01_core/services/mdi-router.service';
import routes from './app.mdi-routes';
import { MDIRouteActivation } from './05_layout/feature-instance/feature-instance.component';
import { environment } from '../environments/environment';


type LocalConfigData = {
  Environment: string,
  Backend_URL: string,
  ClientId_Source_URL: string,
  FileService_URL: string,
  Frontend_URL: string,
  TempCamMasterDb_URL: string
};
const LOCAL_CONFIG_PATH = '/SPA_Config.json';


@Injectable({ providedIn: 'root' })
export class ConfigService {
  private readonly httpClient = inject(HttpClient);

  async loadConfig(): Promise<void> {

    const config = await firstValueFrom(
      this.httpClient.get<Partial<LocalConfigData>>(LOCAL_CONFIG_PATH).pipe(
        // catchError cannot avoid the console error log
        catchError(() => {
          console.log('No local configuration data found. Using build-time defaults from environment.ts.')
          // console.log(`Error loading configuration file ${LOCAL_CONFIG_PATH}. Using build-time defaults from environment.ts.`);
          return of({} as Partial<LocalConfigData>);
        }),
      ),
    );

    if (!config || Object.keys(config).length === 0) {
      console.log('No local configuration data found. Using build-time defaults from environment.ts.');
      // console.log(`No configuration data found in ${LOCAL_CONFIG_PATH}. Using build-time defaults from environment.ts.`);
      return;
    }

    console.log('Local configuration loaded');
    // console.log('Loaded configuration:', JSON.stringify(config));

    // Override build-time defaults with runtime settings from SPA_Config.json.
    if (config.Environment) {
      environment.name = config.Environment;
    }

    if (config.Backend_URL) {
      environment.baseUrl = config.Backend_URL;
    }

    if (config.ClientId_Source_URL) {
      environment.clientIDSourceUrl = config.ClientId_Source_URL;
    }

    if (config.FileService_URL) {
      environment.fileServiceUrl = config.FileService_URL;
    }

    if (config.Frontend_URL) {
      environment.authCodeFlowConfig.logoutUrl = config.Frontend_URL;
      environment.authCodeFlowConfig.redirectUri = config.Frontend_URL;
    }

    if (config.TempCamMasterDb_URL) {
      environment.tempCamMasterDbUrl = config.TempCamMasterDb_URL;
    }
  }
}


export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    provideHttpClient(),
    provideI18n(),
    provideOAuthClient(),
    provideMDIRouter(routes),
    { provide: CDK_DRAG_CONFIG, useValue: { previewClass: 'dark-mode' } },
    { provide: MDIRouteActivation, useValue: <Activation>{params: {}, queryParams: {}} },
    provideAppInitializer(() => inject(ConfigService).loadConfig()),
  ]
};
